// src/components/CierreDeCaja.jsx
import React, { useState, useEffect } from 'react';
import api from '../api/api';
import moment from 'moment-timezone';
import 'moment/locale/es';
import '../styles/CierreDeCaja.css';

// IMPORTS PARA GENERAR WORD
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  TabStopType,
} from 'docx';
import { saveAs } from 'file-saver';

const CierreDeCaja = () => {
  const [selectedDate, setSelectedDate] = useState(() =>
    moment().tz('America/Costa_Rica').format('YYYY-MM-DD')
  );
  const [tempDate, setTempDate] = useState(selectedDate);

  const [orders, setOrders] = useState([]);
  const [cajaChicaEntries, setCajaChicaEntries] = useState([]);
  const [sinpeEntrantesEntries, setSinpeEntrantesEntries] = useState([]);

  const [loading, setLoading] = useState(false);

  const [cajaChicaForm, setCajaChicaForm] = useState({
    tipo: 'Ingreso',
    descripcion: '',
    monto: '',
  });

  const [sinpeEntrantesForm, setSinpeEntrantesForm] = useState({
    banco: '',
    quien: '',
    motivo: '',
    monto: '',
    afectaCPC: false,
  });

  const [selectedOrder, setSelectedOrder] = useState(null);

  const [showFactModal, setShowFactModal] = useState(false);
  const [factForm, setFactForm] = useState({
    orderId: null,
    fact: '',
  });

  useEffect(() => {
    fetchOrdersForDate(selectedDate);
    fetchCajaChicaEntries(selectedDate);
    fetchSinpeEntrantesEntries(selectedDate);
  }, [selectedDate]);

  const fetchOrdersForDate = async (date) => {
    try {
      setLoading(true);
      const res = await api.get(`/api/cierre-caja?date=${date}`);
      setOrders(res.data);
    } catch (err) {
      console.error('Error al obtener los pedidos:', err);
      alert('No se pudieron cargar los pedidos para la fecha seleccionada.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCajaChicaEntries = async (date) => {
    try {
      const res = await api.get(`/api/caja-chica?date=${date}`);
      setCajaChicaEntries(res.data);
    } catch (err) {
      console.error('Error al obtener movimientos de caja chica:', err);
    }
  };

  const fetchSinpeEntrantesEntries = async (date) => {
    try {
      const res = await api.get(`/api/sinpe-entrantes?date=${date}`);
      setSinpeEntrantesEntries(res.data);
    } catch (err) {
      console.error('Error al obtener SINPES entrantes:', err);
    }
  };

  const handleSearch = () => {
    setSelectedDate(tempDate);
  };

  const handlePrint = () => {
    window.print();
  };

  const formatHeaderPath = (date) => {
    const d = moment.tz(date, 'America/Costa_Rica');
    return `Cajas → ${d.format('YYYY')} → ${d.format('MMMM')} → ${d.format('DD')}`;
  };

  const formatCurrency = (amt) =>
    new Intl.NumberFormat('es-CR', { style: 'currency', currency: 'CRC' }).format(amt);

  const orderHasIVA = (order) => {
    const ivaValue = (order.iva || '').toString().trim().toLowerCase();
    if (!ivaValue || ivaValue === 'no' || ivaValue === 'n/a' || ivaValue === 'na') return false;
    return true;
  };

  // Resúmenes pedidos
  const totalOrders = orders.length;
  const totalAmount = orders.reduce((sum, o) => sum + parseFloat(o.totalPayment || 0), 0);

  const paymentMethodSums = {};
  orders.forEach((o) => {
    const m = o.formaPago || 'Desconocido';
    paymentMethodSums[m] = (paymentMethodSums[m] || 0) + parseFloat(o.totalPayment || 0);
  });

  // Resúmenes caja chica
  const totalIngresos = cajaChicaEntries
    .filter((e) => e.tipo === 'Ingreso')
    .reduce((sum, e) => sum + parseFloat(e.monto || 0), 0);

  const totalEgresos = cajaChicaEntries
    .filter((e) => e.tipo === 'Egreso')
    .reduce((sum, e) => sum + parseFloat(e.monto || 0), 0);

  const totalCajaChica = totalIngresos - totalEgresos;

  // Resumen sinpes
  const totalSinpeEntrantes = sinpeEntrantesEntries.reduce(
    (sum, e) => sum + parseFloat(e.monto || 0),
    0
  );

  // Tab stops para Word
  const tabStops = [
    { type: TabStopType.LEFT, position: 1440 },
    { type: TabStopType.LEFT, position: 2880 },
    { type: TabStopType.LEFT, position: 4320 },
    { type: TabStopType.LEFT, position: 5760 },
  ];

  // Descargar en Word (sin cambios)
  const handleDownload = async () => {
    const fechaForm = moment(selectedDate).tz('America/Costa_Rica').format('DD-MM-YY');
    const children = [];

    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `CIERRE DE CAJA ${fechaForm} EJECUTIV@: `,
            bold: true,
            size: 40,
            font: 'Calibri',
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 300 },
      })
    );

    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: 'N° Pedido', bold: true, size: 24, font: 'Calibri' }),
          new TextRun('\t'),
          new TextRun({ text: 'Cliente', bold: true, size: 24, font: 'Calibri' }),
          new TextRun('\t'),
          new TextRun('\t'),
          new TextRun({ text: 'F. de Pago', bold: true, size: 24, font: 'Calibri' }),
          new TextRun('\t'),
          new TextRun('\t'),
          new TextRun({ text: 'Monto', bold: true, size: 24, font: 'Calibri' }),
        ],
        tabStops,
        spacing: { after: 200 },
      })
    );

    orders.forEach((o) => {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: o.orderNumber, size: 24, font: 'Calibri' }),
            new TextRun('\t'),
            new TextRun({
              text: o.customer === 'vacío' ? o.para : o.customer,
              size: 24,
              font: 'Calibri',
            }),
            new TextRun('\t'),
            new TextRun('\t'),
            new TextRun({ text: o.formaPago, size: 24, font: 'Calibri' }),
            new TextRun('\t'),
            new TextRun('\t'),
            new TextRun({
              text: formatCurrency(o.totalPayment || 0),
              size: 24,
              font: 'Calibri',
            }),
          ],
          tabStops,
        })
      );
    });

    children.push(new Paragraph({ text: '', spacing: { after: 300 } }));

    children.push(
      new Paragraph({
        children: [new TextRun({ text: 'Resumen del Día', bold: true, size: 36, font: 'Calibri' })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
      })
    );

    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: 'Total de Pedidos: ', bold: true, size: 24, font: 'Calibri' }),
          new TextRun({ text: `${totalOrders}`, size: 24, font: 'Calibri' }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 100 },
      })
    );

    Object.entries(paymentMethodSums).forEach(([method, sum]) => {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: `Pagos por ${method}: `, bold: true, size: 24, font: 'Calibri' }),
            new TextRun({ text: formatCurrency(sum), size: 24, font: 'Calibri' }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 100 },
        })
      );
    });

    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: 'Monto Total: ', bold: true, size: 24, font: 'Calibri' }),
          new TextRun({ text: formatCurrency(totalAmount), size: 24, font: 'Calibri' }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 300 },
      })
    );

    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: 'Resumen de Caja Chica', bold: true, size: 36, font: 'Calibri' }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
      })
    );

    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: 'Total Ingresos: ', bold: true, size: 24, font: 'Calibri' }),
          new TextRun({ text: formatCurrency(totalIngresos), size: 24, font: 'Calibri' }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 100 },
      })
    );

    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: 'Total Egresos: ', bold: true, size: 24, font: 'Calibri' }),
          new TextRun({ text: formatCurrency(totalEgresos), size: 24, font: 'Calibri' }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 100 },
      })
    );

    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: 'Total en Caja Chica: ', bold: true, size: 24, font: 'Calibri' }),
          new TextRun({ text: formatCurrency(totalCajaChica), size: 24, font: 'Calibri' }),
        ],
        alignment: AlignmentType.CENTER,
      })
    );

    const doc = new Document({ sections: [{ children }] });
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `Caja ${fechaForm}.docx`);
  };

  // Caja chica handlers
  const handleCajaChicaChange = (e) => {
    const { name, value } = e.target;
    setCajaChicaForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCajaChicaSubmit = async (e) => {
    e.preventDefault();
    if (!cajaChicaForm.descripcion || !cajaChicaForm.monto) {
      return alert('Debes completar todos los campos.');
    }
    try {
      await api.post('/api/caja-chica', {
        ...cajaChicaForm,
        fecha: selectedDate,
      });
      alert('Movimiento de caja chica guardado correctamente');
      setCajaChicaForm({ tipo: 'Ingreso', descripcion: '', monto: '' });
      fetchCajaChicaEntries(selectedDate);
    } catch (err) {
      console.error('Error al guardar en caja chica:', err);
      alert('Ocurrió un error al guardar el movimiento');
    }
  };

  // Sinpes handlers
  const handleSinpeEntrantesChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSinpeEntrantesForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSinpeEntrantesSubmit = async (e) => {
    e.preventDefault();
    if (!sinpeEntrantesForm.banco || !sinpeEntrantesForm.quien || !sinpeEntrantesForm.monto) {
      return alert('Debes completar Banco, Quién y Monto.');
    }

    try {
      await api.post('/api/sinpe-entrantes', {
        fecha: selectedDate,
        banco: sinpeEntrantesForm.banco,
        quien: sinpeEntrantesForm.quien,
        motivo: sinpeEntrantesForm.motivo || '',
        monto: sinpeEntrantesForm.monto,
        afectaCPC: !!sinpeEntrantesForm.afectaCPC,
      });

      alert('SINPE entrante guardado correctamente');
      setSinpeEntrantesForm({
        banco: '',
        quien: '',
        motivo: '',
        monto: '',
        afectaCPC: false,
      });
      fetchSinpeEntrantesEntries(selectedDate);
    } catch (err) {
      console.error('Error al guardar SINPE entrante:', err);
      alert('Ocurrió un error al guardar el SINPE entrante');
    }
  };

  // Fact modal handlers
  const openFactModal = (order) => {
    setFactForm({
      orderId: order.id,
      fact: order.fact || '',
    });
    setShowFactModal(true);
  };

  const closeFactModal = () => {
    setShowFactModal(false);
    setFactForm({ orderId: null, fact: '' });
  };

  const handleFactInputChange = (e) => {
    const onlyDigits = e.target.value.replace(/\D/g, '').slice(0, 6);
    setFactForm((prev) => ({ ...prev, fact: onlyDigits }));
  };

  const handleSaveFact = async (e) => {
    e.preventDefault();
    if (!factForm.orderId) return;

    try {
      await api.put(`/api/orders/${factForm.orderId}/fact`, { fact: factForm.fact || null });
      alert('Número de factura guardado correctamente');
      await fetchOrdersForDate(selectedDate);
      closeFactModal();
    } catch (error) {
      console.error('Error al guardar número de factura:', error);
      alert('Error al guardar número de factura');
    }
  };

  return (
    <div className="cierre-caja-container">
      <h1>Cierre de Caja</h1>

      <div className="cierre-caja-date-picker">
        <label htmlFor="date">Seleccionar Fecha:</label>
        <input
          type="date"
          id="date"
          value={tempDate}
          onChange={(e) => setTempDate(e.target.value)}
        />
        <button className="buscar-button2" onClick={handleSearch}>
          Buscar
        </button>
      </div>

      <h2 className="cierre-caja-header">{formatHeaderPath(selectedDate)}</h2>

      <div className="cierre-caja-actions">
        <button className="print-button2" onClick={handlePrint}>
          Imprimir
        </button>
        <button className="print-button2" onClick={handleDownload}>
          Descargar
        </button>
      </div>

      {loading ? (
        <p className="loading-text">Cargando pedidos...</p>
      ) : (
        <div className="cierre-caja-table-container">
          {orders.length === 0 ? (
            <p className="no-orders-text">No se han registrado pedidos para esta fecha.</p>
          ) : (
            <>
              {/* PEDIDOS */}
              <table className="cierre-caja-table">
                <thead>
                  <tr>
                    <th>N° Pedido</th>
                    <th>N° Fact</th>
                    <th>Cliente</th>
                    <th>F. de Pago</th>
                    <th>Fuente</th>
                    <th>Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td>
                        <button
                          className="order-number-button"
                          onClick={() => setSelectedOrder(order)}
                        >
                          {order.orderNumber}
                        </button>
                      </td>

                      <td className="fact-cell">
                        {orderHasIVA(order) ? (
                          order.fact ? (
                            <button
                              type="button"
                              className="fact-edit-button"
                              onClick={() => openFactModal(order)}
                              title="Editar número de factura"
                            >
                              {order.fact}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="fact-add-button"
                              onClick={() => openFactModal(order)}
                            >
                              Añadir
                            </button>
                          )
                        ) : (
                          <span className="fact-na">N/A</span>
                        )}
                      </td>

                      <td>{order.customer === 'vacío' ? order.para : order.customer}</td>
                      <td>{order.formaPago}</td>
                      <td>{order.fuente}</td>
                      <td>{formatCurrency(order.totalPayment || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* RESUMEN DEL DÍA (inmediatamente después de pedidos) */}
              <div className="cierre-caja-summary" style={{ textAlign: 'center' }}>
                <h3>Resumen del Día</h3>
                <p>Total de Pedidos: {totalOrders}</p>
                {Object.keys(paymentMethodSums).map((method) => (
                  <p key={method}>
                    Pagos por {method}: {formatCurrency(paymentMethodSums[method])}
                  </p>
                ))}
                <p>Monto Total: {formatCurrency(totalAmount)}</p>
              </div>

              {/* CAJA CHICA (formulario + tabla + resumen juntos) */}
              <div className="caja-chica-section">
                <div className="caja-chica-form" style={{ textAlign: 'center' }}>
                  <h3>Agregar Movimiento Caja Chica</h3>
                  <form onSubmit={handleCajaChicaSubmit}>
                    <select name="tipo" value={cajaChicaForm.tipo} onChange={handleCajaChicaChange}>
                      <option value="Ingreso">Ingreso</option>
                      <option value="Egreso">Egreso</option>
                    </select>
                    <input
                      type="text"
                      name="descripcion"
                      placeholder="Descripción"
                      value={cajaChicaForm.descripcion}
                      onChange={handleCajaChicaChange}
                    />
                    <input
                      type="number"
                      name="monto"
                      placeholder="Monto"
                      value={cajaChicaForm.monto}
                      onChange={handleCajaChicaChange}
                    />
                    <button type="submit">Guardar</button>
                  </form>
                </div>

                {cajaChicaEntries.length > 0 && (
                  <>
                    <h3 className="caja-chica-header">Resumen de Caja Chica</h3>
                    <table className="cierre-caja-table">
                      <thead>
                        <tr>
                          <th>Tipo</th>
                          <th>Descripción</th>
                          <th>Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cajaChicaEntries.map((e) => (
                          <tr key={e.id}>
                            <td>{e.tipo}</td>
                            <td>{e.descripcion}</td>
                            <td>{formatCurrency(e.monto)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div className="cierre-caja-summary" style={{ textAlign: 'center' }}>
                      <h3>Resumen de Caja Chica</h3>
                      <p>Total Ingresos: {formatCurrency(totalIngresos)}</p>
                      <p>Total Egresos: {formatCurrency(totalEgresos)}</p>
                      <p>Total en Caja Chica: {formatCurrency(totalCajaChica)}</p>
                    </div>
                  </>
                )}
              </div>

              {/* SINPES (formulario + tabla + resumen juntos) */}
              <div className="sinpe-section">
                <div className="caja-chica-form" style={{ textAlign: 'center' }}>
                  <h3>Sinpes Entrantes</h3>
                  <form onSubmit={handleSinpeEntrantesSubmit}>
                    <select
                      name="banco"
                      value={sinpeEntrantesForm.banco}
                      onChange={handleSinpeEntrantesChange}
                    >
                      <option value="">Seleccione Banco</option>
                      <option value="BAC 6024">BAC 6024</option>
                      <option value="BCR 0000">BCR 0000</option>
                    </select>

                    <input
                      type="text"
                      name="quien"
                      placeholder="Quién"
                      value={sinpeEntrantesForm.quien}
                      onChange={handleSinpeEntrantesChange}
                    />

                    <input
                      type="text"
                      name="motivo"
                      placeholder="Motivo"
                      value={sinpeEntrantesForm.motivo}
                      onChange={handleSinpeEntrantesChange}
                    />

                    <input
                      type="number"
                      name="monto"
                      placeholder="Monto (colones)"
                      value={sinpeEntrantesForm.monto}
                      onChange={handleSinpeEntrantesChange}
                    />

                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="checkbox"
                        name="afectaCPC"
                        checked={!!sinpeEntrantesForm.afectaCPC}
                        onChange={handleSinpeEntrantesChange}
                      />
                      Afecta CPC?
                    </label>

                    <button type="submit">Guardar</button>
                  </form>
                </div>

                {sinpeEntrantesEntries.length > 0 && (
                  <>
                    <h3 className="caja-chica-header">Resumen de Sinpes Entrantes</h3>
                    <table className="cierre-caja-table">
                      <thead>
                        <tr>
                          <th>Banco</th>
                          <th>Quién</th>
                          <th>Motivo</th>
                          <th>Monto</th>
                          <th>Afecta CPC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sinpeEntrantesEntries.map((e) => (
                          <tr key={e.id}>
                            <td>{e.banco}</td>
                            <td>{e.quien}</td>
                            <td>{e.motivo}</td>
                            <td>{formatCurrency(e.monto)}</td>
                            <td>{e.afecta_cpc ? 'Sí' : 'No'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div className="cierre-caja-summary" style={{ textAlign: 'center' }}>
                      <h3>Resumen de Sinpes Entrantes</h3>
                      <p>Total de Sinpes Entrantes: {formatCurrency(totalSinpeEntrantes)}</p>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Modal de detalle de pedido */}
      {selectedOrder && (
        <div className="order-modal-overlay">
          <div className="order-modal">
            <h2>Detalle del Pedido: {selectedOrder.orderNumber}</h2>
            <p>
              <strong>Ejecutiv@:</strong> {selectedOrder.ejecutivo}
            </p>
            <p>
              <strong>Cliente:</strong>{' '}
              {selectedOrder.customer === 'vacío' ? selectedOrder.para : selectedOrder.customer}
            </p>
            <p>
              <strong>Fuente:</strong> {selectedOrder.fuente}
            </p>
            <p>
              <strong>Para:</strong> {selectedOrder.para}
            </p>
            <p>
              <strong>Entregado El:</strong>{' '}
              {moment(selectedOrder.scheduledTime).tz('America/Costa_Rica').format('DD/MM/YYYY h:mma')}
            </p>
            <p>
              <strong>Teléfono:</strong> {selectedOrder.telefono}
            </p>
            <p>
              <strong>Detalle de lo entregado:</strong> {selectedOrder.detalleEntrega}
            </p>
            <p>
              <strong>Entregado En:</strong> {selectedOrder.entregarEn}
            </p>
            <p>
              <strong>Tarjeta:</strong> {selectedOrder.tarjeta}
            </p>
            <p>
              <strong>Forma de Pago:</strong> {selectedOrder.formaPago}
            </p>
            <p>
              <strong>IVA:</strong> {formatCurrency(selectedOrder.iva || 0)}
            </p>
            <p>
              <strong>Monto:</strong> {formatCurrency(selectedOrder.totalPayment || 0)}
            </p>
            <button className="close-modal-button" onClick={() => setSelectedOrder(null)}>
              Cerrar
            </button>
          </div>
        </div>
      )}

      {/* MODAL NÚMERO DE FACTURA */}
      {showFactModal && (
        <div className="modal-overlay">
          <div className="modal fact-modal">
            <h3>Asignar número de factura</h3>
            <form onSubmit={handleSaveFact}>
              <label htmlFor="factNumber">N° de factura (4–6 dígitos):</label>
              <input
                id="factNumber"
                type="text"
                value={factForm.fact}
                onChange={handleFactInputChange}
                maxLength={6}
                required
              />

              <div className="fact-modal-buttons">
                <button type="submit" className="fact-save-button">
                  Guardar
                </button>
                <button type="button" className="fact-cancel-button" onClick={closeFactModal}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CierreDeCaja;
